import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw } from 'lucide-react';

import type { SafetyStockRunListItem, SafetyStockRunStatus } from '@/services/safetyStockService';

interface Props {
  run: SafetyStockRunListItem | null;
  detail?: SafetyStockRunStatus | null;
  loading?: boolean;
  applyEnabled: boolean;
  applyRunning: boolean;
  itemCount?: number | null;
  noEngineRun?: boolean;
  policyNotice?: string | null;
  onRecalculate: () => void;
  onViewProgress?: () => void;
}

export function RunStatusBar({
  run,
  detail,
  loading,
  applyEnabled,
  applyRunning,
  itemCount,
  noEngineRun,
  policyNotice,
  onRecalculate,
  onViewProgress,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'es';

  // While an apply is in flight, never reuse the previous SUCCESS run for progress.
  const status = applyRunning
    ? detail?.status || 'RUNNING'
    : detail?.status || run?.status || '—';
  const isRunning = status === 'RUNNING' || applyRunning;
  const started = detail?.started_at || (!applyRunning ? run?.started_at : null);
  const finished = applyRunning
    ? null
    : detail?.finished_at || run?.finished_at;
  const whenIso = finished || started;
  const ok = applyRunning
    ? detail?.successful_items ?? 0
    : detail?.successful_items ?? run?.successful_items ?? 0;
  const fail = applyRunning
    ? detail?.failed_items ?? 0
    : detail?.failed_items ?? run?.failed_items ?? 0;
  const total = applyRunning
    ? detail?.total_items ?? itemCount ?? 0
    : detail?.total_items ?? run?.total_items ?? 0;
  const done = ok + fail;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  const formatWhen = (iso?: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(locale, {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  let statusLine: ReactNode;
  if (loading && !applyRunning) {
    statusLine = (
      <span className="inline-flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('safetyStock.summary.loading')}
      </span>
    );
  } else if (noEngineRun && !applyRunning) {
    statusLine = (
      <span className="text-sm text-gray-600">
        {policyNotice || t('safetyStock.summary.runStatus.noRun')}
      </span>
    );
  } else {
    statusLine = (
      <p className="text-sm text-gray-700">
        <span className="font-medium text-gray-900">
          {t('safetyStock.summary.runStatus.title')}
        </span>
        <span className="text-gray-400"> · </span>
        <span className="tabular-nums">
          {applyRunning && !whenIso
            ? t('safetyStock.summary.runStatus.inProgress')
            : formatWhen(whenIso)}
        </span>
        <span className="text-gray-400"> · </span>
        <span className="font-medium text-gray-900">{status}</span>
      </p>
    );
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">{statusLine}</div>
        <div className="flex flex-wrap items-center gap-2">
          {applyRunning && onViewProgress ? (
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              onClick={onViewProgress}
            >
              <Loader2 className="h-4 w-4 animate-spin text-stocker-primary" />
              {t('safetyStock.apply.viewProgress')}
            </button>
          ) : null}
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md bg-stocker-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            disabled={!applyEnabled || isRunning}
            title={
              !applyEnabled
                ? t('safetyStock.apply.errors.applyDisabled')
                : isRunning
                  ? t('safetyStock.summary.runStatus.runningDisabled')
                  : undefined
            }
            onClick={onRecalculate}
          >
            {isRunning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {t('safetyStock.summary.runStatus.recalculate', {
              count: typeof itemCount === 'number' ? itemCount : 0,
            })}
          </button>
        </div>
      </div>
      {isRunning ? (
        <div className="mt-2">
          <div className="mb-1 flex justify-between text-xs text-gray-500">
            <span>{t('safetyStock.summary.runStatus.progress')}</span>
            <span className="tabular-nums">
              {total > 0
                ? `${done}/${total} (${pct}%)`
                : t('safetyStock.summary.runStatus.progressStarting')}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-stocker-primary transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
